import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, Loader2, MapPin, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { GoogleSignInButton } from "../pages/Login";
import { createOrder, formatINR } from "../lib/api";

const EMPTY = { name: "", phone: "", address: "", landmark: "", pincode: "", delivery_note: "" };

export default function CheckoutModal() {
  const { isCheckoutOpen, setCheckoutOpen, items, count, total, clear } = useCart();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (user && form.name === EMPTY.name) {
      setForm((f) => ({ ...f, name: f.name || user.name || "", phone: f.phone }));
    }
  }, [user]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Tell us your name";
    if (!/^[6-9]\d{9}$/.test(form.phone.trim())) errs.phone = "Enter a valid 10-digit mobile number";
    if (form.address.trim().length < 10) errs.address = "Add your full delivery address";
    if (!/^\d{6}$/.test(form.pincode.trim())) errs.pincode = "6-digit pincode";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    setApiError(null);
    try {
      const order = await createOrder({
        customer: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          landmark: form.landmark.trim() || null,
          pincode: form.pincode.trim(),
          delivery_note: form.delivery_note.trim() || null,
          email: user?.email || null,
        },
        items: items.map((i) => ({ product_id: i.product.id, qty: i.qty })),
      });
      setPlaced(order);
      clear();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setCheckoutOpen(false);
    if (placed) {
      setPlaced(null);
      setForm(EMPTY);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-starlight/15 bg-white/5 px-4 py-3 text-sm text-starlight placeholder:text-muted-night/50 outline-none transition-colors focus:border-ember/70";

  return (
    <AnimatePresence>
      {isCheckoutOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[70] bg-obsidian/80 backdrop-blur-sm"
            data-testid="checkout-overlay"
          />
          <div className="fixed inset-0 z-[71] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="pointer-events-auto max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-ember/25 bg-obsidian p-6 sm:p-8"
              data-testid="checkout-modal"
              data-lenis-prevent
            >
              {placed ? (
                <div className="text-center py-6">
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.1 }}
                    className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-discount-green/50 bg-discount-green/10"
                  >
                    <BadgeCheck className="h-10 w-10 text-discount-green" />
                  </motion.span>
                  <h3 className="mt-6 font-display text-2xl font-bold text-starlight">Order placed!</h3>
                  <p className="mt-2 font-mono text-2xl font-bold tracking-[0.1em] text-ember" data-testid="order-confirmation-number">
                    {placed.order_number}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-muted-night">
                    {count > 0 ? "" : `${placed.items.length} item${placed.items.length === 1 ? "" : "s"} • `}
                    {formatINR(placed.total)} payable on delivery. We'll call{" "}
                    <span className="text-starlight">{placed.customer.phone}</span> shortly to confirm
                    and fix a delivery slot.
                  </p>
                  <button
                    data-testid="order-done-button"
                    onClick={close}
                    className="mt-8 rounded-full bg-ember px-10 py-3.5 font-mono text-xs font-bold tracking-[0.22em] uppercase text-obsidian hover:bg-marigold transition-colors"
                  >
                    Done
                  </button>
                  <a
                    href={`/track?uc=${placed.order_number}`}
                    data-testid="track-order-link"
                    className="mt-4 block font-mono text-[11px] tracking-[0.2em] uppercase text-muted-night hover:text-ember transition-colors"
                  >
                    Track this order
                  </a>
                </div>
              ) : (
                <>
                  {!user && (
                    <div className="mb-5 rounded-2xl border border-starlight/15 bg-white/5 px-4 py-3">
                      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night mb-2">Checking out as guest</p>
                      <GoogleSignInButton redirectTo="/" label="Sign in with Google for faster checkout" />
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-ember">Cash on delivery</p>
                      <h3 className="mt-2 font-display text-2xl font-bold text-starlight">Where's the party?</h3>
                    </div>
                    <button
                      data-testid="checkout-close-button"
                      onClick={close}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-starlight/15 text-muted-night hover:border-ember/60 hover:text-ember transition-colors"
                      aria-label="Close checkout"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Full name</label>
                      <input
                        data-testid="checkout-input-name"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your name"
                        className={`mt-1.5 ${inputCls}`}
                      />
                      {errors.name && <p className="mt-1 text-xs text-crimson">{errors.name}</p>}
                    </div>

                    <div>
                      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">WhatsApp mobile</label>
                      <input
                        data-testid="checkout-input-phone"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                        placeholder="10-digit mobile number"
                        inputMode="numeric"
                        className={`mt-1.5 ${inputCls}`}
                      />
                      {errors.phone && <p className="mt-1 text-xs text-crimson">{errors.phone}</p>}
                    </div>

                    <div>
                      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Delivery address</label>
                      <textarea
                        data-testid="checkout-input-address"
                        value={form.address}
                        onChange={set("address")}
                        placeholder="House / street / area, city"
                        rows={3}
                        className={`mt-1.5 ${inputCls} resize-none`}
                      />
                      {errors.address && <p className="mt-1 text-xs text-crimson">{errors.address}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Pincode</label>
                        <input
                          data-testid="checkout-input-pincode"
                          value={form.pincode}
                          onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                          placeholder="6 digits"
                          inputMode="numeric"
                          className={`mt-1.5 ${inputCls}`}
                        />
                        {errors.pincode && <p className="mt-1 text-xs text-crimson">{errors.pincode}</p>}
                      </div>
                      <div>
                        <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Landmark (optional)</label>
                        <input
                          data-testid="checkout-input-landmark"
                          value={form.landmark}
                          onChange={set("landmark")}
                          placeholder="Near…"
                          className={`mt-1.5 ${inputCls}`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Delivery note (optional)</label>
                      <input
                        data-testid="checkout-input-delivery-note"
                        value={form.delivery_note}
                        onChange={set("delivery_note")}
                        placeholder="Preferred date or time…"
                        className={`mt-1.5 ${inputCls}`}
                      />
                    </div>

                    <div className="flex items-center justify-between rounded-2xl border border-ember/25 bg-ember/5 px-4 py-3">
                      <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-muted-night">
                        <MapPin className="h-3.5 w-3.5 text-ember" /> {count} item{count === 1 ? "" : "s"} to deliver
                      </span>
                      <span className="font-mono text-lg font-bold text-ember" data-testid="checkout-total">
                        {formatINR(total)}
                      </span>
                    </div>

                    {apiError && (
                      <p className="rounded-xl border border-crimson/40 bg-crimson/10 px-4 py-3 text-sm text-starlight" data-testid="checkout-error">
                        {apiError}
                      </p>
                    )}

                    <button
                      data-testid="place-cod-order-button"
                      type="submit"
                      disabled={submitting || items.length === 0}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-ember py-4 font-mono text-xs font-bold tracking-[0.22em] uppercase text-obsidian transition-all duration-300 hover:bg-marigold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Placing order…
                        </>
                      ) : (
                        "Place order • Pay on delivery"
                      )}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
